/**
 * Usage limit helpers — observability only (Stage 4B.2).
 * Keep free-tier targets in sync with backend/services/usage_limits.py
 */

export const FREE_USAGE_LIMITS = {
  archives: 1,
  people: 3,
  memories: 10,
  photos: 20,
  voice_recordings: 3,
  transcription_minutes: 5,
  ai_polish_uses: 3,
};

const NEAR_LIMIT_RATIO = 0.8;

export function isNearLimit(usage, limit) {
  if (limit == null || limit <= 0) return false;
  const u = Number(usage) || 0;
  if (u >= limit) return true;
  if (limit <= 5) return u >= Math.max(0, limit - 1);
  return u >= limit * NEAR_LIMIT_RATIO;
}

export function isOverLimit(usage, limit) {
  if (limit == null) return false;
  return (Number(usage) || 0) > limit;
}

export function getUsageHeadline(snapshot) {
  if (!snapshot?.usage) return null;
  const plan = snapshot.plan_type || 'free';
  if (plan !== 'free') {
    return 'You’re building your family archive.';
  }
  const flags = snapshot.soft_flags || {};
  if (flags.people_near_limit || flags.memories_near_limit || flags.photos_near_limit) {
    return 'You’re close to the free starter range.';
  }
  return 'You’re building your free archive.';
}

export function getUsageSubtext(snapshot) {
  if (snapshot?.soft_flags?.upgrade_prompts_coming_soon !== false) {
    return 'Gentle upgrade guidance may appear as you build — nothing is blocked today.';
  }
  return null;
}

export function isFreePlan(snapshot) {
  return (snapshot?.plan_type || 'free') === 'free';
}

/** Add Person moment — free tier at or beyond starter loved-one range. */
export function shouldShowPeopleGrowthPrompt(snapshot) {
  if (!isFreePlan(snapshot)) return false;
  const flags = snapshot?.soft_flags || {};
  return Boolean(flags.people_near_limit || flags.people_over_limit);
}

/** Legacy Book PDF export area — preview included, founding offer for finished export. */
export function shouldShowPdfExportPrompt(snapshot) {
  if (!isFreePlan(snapshot)) return false;
  return Boolean(snapshot?.soft_flags?.pdf_export_upgrade_recommended);
}

/** Memorial publish workspace — lifetime add-on guidance for free archives. */
export function shouldShowMemorialLifetimePrompt(snapshot) {
  return isFreePlan(snapshot);
}

/** Voice / transcription / AI polish areas — Family Archive guidance for free users. */
export function shouldShowFamilyArchiveVoicePrompt(snapshot) {
  return isFreePlan(snapshot);
}

export function formatUsagePair(usage, limit, label) {
  if (limit == null) {
    return `${label}: ${usage ?? 0}`;
  }
  return `${label}: ${usage ?? 0} of ${limit}`;
}

export function pickPrimaryUsageLines(snapshot, maxLines = 3) {
  if (!snapshot?.usage || !snapshot?.limits) return [];
  const pairs = [
    ['people', 'Loved ones'],
    ['memories', 'Memories'],
    ['photos', 'Photos & documents'],
    ['voice_recordings', 'Voice recordings'],
  ];
  const lines = [];
  for (const [key, label] of pairs) {
    const usage = snapshot.usage[key];
    const limit = snapshot.limits[key];
    if (usage == null && limit == null) continue;
    lines.push({
      key,
      label,
      usage: usage ?? 0,
      limit,
      near: isNearLimit(usage, limit),
      over: isOverLimit(usage, limit),
      text: formatUsagePair(usage, limit, label),
    });
  }
  return lines.slice(0, maxLines);
}
